import { IModuleConfiguration, ModuleConfiguration } from 'src/types/system_types';
import { TimeTracker } from '../support/interaction/time_tracker';

import ess from'../../../pip/library/pi-ess/pi-ess.js';
import tt from'../../../pip/library/pi-tt/pi-tt.js';
import { HTTPInterceptor } from 'src/support/network/http_interceptor';

declare global {
  interface Window {
    ['HIP']: any;
  }
}

type t_prompt_control = 'yes' | 'no';

export interface IInteractionModuleConfiguration extends IModuleConfiguration {
  user_idle_time: number;
  keep_alive: {
    on_network_activity: boolean;
    on_human_activity: boolean;
  };
  features: {
    session_timeout_dialog: boolean;
  };
  timeout_dialog: {
    title: string;
    description: string;
    controls: t_prompt_control[];
  };
}

export class InteractionModule extends ModuleConfiguration {
  static SESSION_INFINITE = -1;

  configuration: IInteractionModuleConfiguration;

  private __interactedHandlerBound: (e: Event) => void;
  private __timeTracker: TimeTracker;

  get timeTracker() {
    return this.__timeTracker;
  }

  /**
   *
   * @param configuration
   */
  initialize(configuration: IInteractionModuleConfiguration) {
    return new Promise((resolve) => {
      this.configuration = this.__defaultConfiguration(configuration);

      if (this.configuration.user_idle_time === InteractionModule.SESSION_INFINITE) {
        return resolve(this.configuration);
      }

      this.__createTimeTracker(this.configuration);
      this.__sessionResetMitigation(this);

      if (this.configuration.features.session_timeout_dialog) {
        this.__noWindowActivityMitigation();
      }

      resolve(this.configuration);
    });
  }

  activate() {
    return new Promise((resolve) => {
      if (this.configuration.user_idle_time === InteractionModule.SESSION_INFINITE) {
        return resolve();
      }

      if (tt.get('keep_alive.on_human_activity', this.configuration)) {
        this.__trackUserInteraction();
      }
      if (tt.get('keep_alive.on_network_activity', this.configuration)) {
        this.__trackNetworkActivity();
      }

      resolve();
    });
  }

  uninitialize() {
    this.__untrackUserInteraction();
    this.__untrackInactiveTime();
  }

  private __defaultConfiguration(configuration: IInteractionModuleConfiguration) {
    return tt.mergeWith(
      'clobber',
      {
        user_idle_time: InteractionModule.SESSION_INFINITE,
        keep_alive: {
          on_network_activity: true,
          on_human_activity: true
        },
        features: {
          session_timeout_dialog: true
        },
        timeout_dialog: {
          title: 'Session Timeout',
          description: `You have not been using the application and will be logged out in five seconds. Do you want to continue the session?`,
          controls: ['yes', 'no']
        }
      },
      configuration
    ) as IInteractionModuleConfiguration;
  }

  /**
   *
   * @param configuration
   */
  private __createTimeTracker(configuration: IInteractionModuleConfiguration) {
    this.__timeTracker = new TimeTracker(configuration.user_idle_time, this.__noActivityNotification).restart();
  }

  private __noActivityNotification(event: Event) {
    ess.warning({
      ns: 'ahp',
      site: 'session',
      action: 'no_activity',
      source: (event.target || '--').toString(),
      data: event
    });
  }

  /**
   *
   * @param configuration
   */
  private __untrackInactiveTime() {
    this.timeTracker.stop();
  }

  /**
   *
   * @param configuration
   */
  private __trackUserInteraction() {
    this.__interactedHandlerBound = this.__interactedHandler.bind(this);

    window.addEventListener('mousemove', this.__interactedHandlerBound, true);
    window.addEventListener('mouseup', this.__interactedHandlerBound, true);
    window.addEventListener('keyup', this.__interactedHandlerBound, true);
    window.addEventListener('focus', this.__interactedHandlerBound, true);
  }

  private __trackNetworkActivity() {
    HTTPInterceptor.instance.beforeHTTP.push({
      fn: <T>(uri: string, payload: T) => {
        this.timeTracker.reset(new CustomEvent('api-request'));
        return payload;
      },
      uriMatcher: []
    });
  }

  private __interactedHandler(event: Event) {
    this.timeTracker.reset(event);
  }

  /**
   *
   * @param configuration
   */
  private __untrackUserInteraction() {
    window.removeEventListener('mousemove', this.__interactedHandlerBound, true);
    window.removeEventListener('mouseup', this.__interactedHandlerBound, true);
    window.removeEventListener('keyup', this.__interactedHandlerBound, true);
    window.removeEventListener('focus', this.__interactedHandlerBound, true);
  }

  private __noWindowActivityMitigation() {
    ess.on(
      {
        ns: 'ahp',
        site: 'session',
        status: 'warning',
        action: 'no_activity'
      },
      () => {
        const prompt = this.configuration.timeout_dialog;

        window.HIP.fn
          .prompt({
            heading: prompt.title,
            text: prompt.description,
            controls: prompt.controls
          })
          .then(() => {
            // call api to continue the session and reset the timer
            ess.success({
              ns: 'ahp',
              source: 'window',
              site: 'session',
              action: 'reset'
            });
          })
          .catch(() => {
            // invoke the logout session
            ess.warning({
              ns: 'ahp',
              source: 'window',
              site: 'session',
              action: 'logout'
            });
          });
      }
    );
  }

  private __sessionResetMitigation(activityTracking: InteractionModule) {
    ess.on(
      {
        ns: 'ahp',
        source: 'window',
        site: 'session',
        action: 'reset'
      },
      () => {
        activityTracking.timeTracker.restart();
      }
    );
  }
}
